/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  SmartForm,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { PortCallCommentDetailsDto, usePortApi } from "~/api/GeneratedApi";
import { CustomQueryKey } from "~/utils/hooks/QueryKeys";
import { useAccess } from "~/utils/hooks/useAccess";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQueryClient } from "~/utils/hooks/useCustomQueryClient";

type PortCallCommentFormType = {
  comment: string;
};

type Props = {
  portId: number;
  mapAreaIds: number[];
  portCallId: number;
  queryKey: CustomQueryKey;
  existingPortCalls: PortCallCommentDetailsDto[] | undefined;
};
export const PortCallCommentForm = ({
  portId,
  mapAreaIds,
  portCallId,
  queryKey,
  existingPortCalls,
}: Props): JSX.Element => {
  const { t } = useTranslation("port-calls");
  const { addPortCallComment, editPortCallComment, deletePortCallComment } =
    usePortApi();
  const { invalidateCustomQueries } = useCustomQueryClient();
  const { getAccess } = useAccess();

  const canEditThisPort = React.useMemo(
    () => getAccess(mapAreaIds).PortUpdate,
    [getAccess, mapAreaIds],
  );

  const { mutate: addComment } = useCustomMutation(
    (d: PortCallCommentFormType) =>
      addPortCallComment(portCallId, {
        comment: d.comment,
        internal: false,
        portId,
      }),
    {
      onSuccess: () => invalidateCustomQueries(queryKey),
    },
  );
  const { mutate: editComment } = useCustomMutation(
    ({ d, commentId }: { d: PortCallCommentFormType; commentId: number }) =>
      editPortCallComment(
        commentId,
        {
          comment: d.comment,
          internal: false,
        },
        portCallId.toString(),
      ),
    {
      onSuccess: () => invalidateCustomQueries(queryKey),
    },
  );
  const { mutate: deleteComment } = useCustomMutation(
    (commentId: number) =>
      deletePortCallComment(commentId, portCallId.toString()),
    {
      onSuccess: () => invalidateCustomQueries(queryKey),
    },
  );

  const existingComment = React.useMemo(
    () => existingPortCalls?.find((p) => p.portCallId === portCallId),
    [portCallId, existingPortCalls],
  );

  const commentFormProps = React.useMemo(
    () => ({
      defaultValues: {
        comment: existingComment?.comment ?? "",
      } as PortCallCommentFormType,
    }),
    [existingComment],
  );
  const formMethods = useSmartForm<PortCallCommentFormType>(commentFormProps);
  const { watch } = formMethods;
  const commentValue = watch("comment");

  const handleSubmit = React.useCallback(
    (d: PortCallCommentFormType) => {
      if (d.comment && !existingComment) addComment(d);
      if (!d.comment && existingComment) deleteComment(existingComment.id);
      if (d.comment && existingComment)
        editComment({ d, commentId: existingComment.id });
    },
    [addComment, deleteComment, editComment, existingComment],
  );

  const buttonText = React.useMemo(() => {
    if (!existingComment) return t("general:add");
    if (!commentValue) return t("general:delete");
    return t("general:save");
  }, [commentValue, existingComment, t]);

  return (
    <SmartForm<PortCallCommentFormType>
      methods={formMethods}
      onSubmit={handleSubmit}
      gridProps={{ direction: "column" }}
    >
      <SmartTextField<PortCallCommentFormType>
        name="comment"
        gridProps={{ xs: true }}
        fullWidth
        multiline
        variant="outlined"
        minRows={3}
        disabled={!canEditThisPort}
        label={t("comment")}
      />
      {canEditThisPort ? <SmartSubmit label={buttonText} /> : null}
    </SmartForm>
  );
};
